import React, { createContext, useState, useEffect, useCallback, useMemo } from 'react';
import { evaluateFlags } from '../services/featureFlagService';

export const FeatureFlagContext = createContext({
    flags: {},
    isLoading: true,
    error: null,
    isEnabled: () => false,
    refetch: () => Promise.resolve()
});

export const FeatureFlagProvider = ({ children }) => {
    const [flags, setFlags] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchFlags = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await evaluateFlags();
            // Handle axios response - data is in response.data
            const flagData = response.data || response;
            setFlags(flagData);
        } catch (err) {
            console.error('Failed to fetch feature flags:', err);
            setError(err);
            // Keep existing flags on error (graceful degradation)
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Don't auto-fetch on mount - wait for MainProtectedLayout to trigger
    // after authentication is confirmed. Components fail closed (default false)
    // until flags are loaded.

    const isEnabled = useCallback((flagKey, defaultValue = false) => {
        if (isLoading) return defaultValue;
        return flags[flagKey] ?? defaultValue;
    }, [flags, isLoading]);

    const value = useMemo(() => ({
        flags,
        isLoading,
        error,
        isEnabled,
        refetch: fetchFlags
    }), [flags, isLoading, error, isEnabled, fetchFlags]);

    return (
        <FeatureFlagContext.Provider value={value}>
            {children}
        </FeatureFlagContext.Provider>
    );
};
